import { Event, EventDates, SingleEvent } from '@/types/EventInterface';
import { rrulestr } from 'rrule';
import { startOfMonth, addMonths, isWithinInterval, isFuture, isSameDay } from 'date-fns';
import type { ProcessedEventData, ProcessedEvent } from '@/types/ProcessedEvent';
import eventDataJson from './eventData.json';

/**
 * Helper to combine a Sanity "YYYY-MM-DD" date with a "HH:mm" string into a Date object.
 * e.g. combineDateAndTime("2025-01-22", "16:00") => 2025-01-22T16:00
 */
function combineDateAndTime(dateString: string, timeString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number); // "2025-01-22" => [2025, 1, 22]
  const [hours, minutes] = timeString.split(':').map(Number); // "16:00" => [16, 0]
  return new Date(year, month - 1, day, hours, minutes);
}

const eventData = eventDataJson as ProcessedEventData;

export const getEvents = async (language: 'en' | 'es'): Promise<ProcessedEvent[]> => {
  return eventData[language];
};

export const getEventDates = (events: SingleEvent[]) => {
  const startDate = startOfMonth(new Date());
  const endDate = addMonths(startDate, 6);
  /**
   * For each date range, parse the RRULE and return an array of valid JS Dates.
   */
  const getDatesForRange = (range: any) => {
    const { startDate: sd, endDate: ed, rrule } = range ?? {};
    if (!sd) return [];

    const start = new Date(sd);
    const end = ed ? new Date(ed) : new Date(sd);

    // If no RRULE, treat this as a single (one-time) date
    if (!rrule?.trim()) {
      return [start];
    }

    try {
      const rule = rrulestr(rrule, { dtstart: start });
      // Return only dates within the start-end window
      return rule.all().filter((date) => date >= start && date <= end);
    } catch (error) {
      console.error('Error parsing rrule:', error);
      return [start];
    }
  };

  /**
   * We'll accumulate objects like:
   * {
   *   event,
   *   date,       // The specific day of occurrence
   *   startTime,  // Base start time from the date range
   *   endTime,    // Base end time from the date range
   *   exception: {
   *     cancelEvent?: boolean,
   *     modifiedStartTime?: Date,
   *     modifiedEndTime?: Date
   *   }
   * }
   */
  const allOccurrences: {
    event: Event;
    date: Date;
    startTime: Date;
    endTime: Date;
    exception?: {
      cancelEvent?: boolean;
      modifiedStartTime?: Date;
      modifiedEndTime?: Date;
    };
  }[] = [];

  // For each event
  for (const event of events) {
    const { eventDates, exceptions } = event;
    if (!eventDates || eventDates.length === 0) continue;

    // Go through each date range
    for (const dateRange of eventDates) {
      const freq = dateRange?.frequency;
      if (!freq) continue;

      // Generate all possible occurrence dates
      const dateList = getDatesForRange(freq)
        // Filter out anything beyond 6 months
        .filter((dt: Date) => isWithinInterval(dt, { start: startDate, end: endDate }))
        // Filter out past dates
        .filter((dt: Date) => isFuture(dt))
        // Sort ascending
        .sort((a: Date, b: Date) => a.getTime() - b.getTime());

      // Convert freq.startDate/freq.endDate into Date objects for default times
      const baseStart = freq.startDate ? new Date(freq.startDate) : null;
      const baseEnd = freq.endDate ? new Date(freq.endDate) : baseStart;

      // For each occurrence date
      for (const occurrenceDate of dateList) {
        let matchedException = null;
        if (exceptions && exceptions.length > 0) {
          matchedException = exceptions.find((ex: any) => {
            const exceptionDate = combineDateAndTime(ex.exceptionDate, '00:00');
            return isSameDay(exceptionDate, occurrenceDate);
          });
        }

        // Build our base occurrence object
        const occurrence = {
          event,
          date: occurrenceDate,
          startTime: baseStart || occurrenceDate,
          endTime: baseEnd || occurrenceDate,
          exception: undefined as any,
        };

        // If there's an exception for this date, parse it
        if (matchedException) {
          // If you still support a "cancelEvent" boolean in the new data:
          const cancel = matchedException.cancelEvent ?? false;

          let modifiedStartTime;
          let modifiedEndTime;

          if (matchedException.timeDuration) {
            const { startTime, endTime } = matchedException.timeDuration;
            // If either start/endTime string is present, combine with exceptionDate
            if (startTime) {
              modifiedStartTime = combineDateAndTime(matchedException.exceptionDate, startTime);
            }
            if (endTime) {
              modifiedEndTime = combineDateAndTime(matchedException.exceptionDate, endTime);
            }
          }

          occurrence.exception = {
            cancelEvent: cancel,
            modifiedStartTime,
            modifiedEndTime,
          };
        }

        allOccurrences.push(occurrence);
      }
    }
  }

  // Sort everything by date ascending
  allOccurrences.sort((a, b) => a.date.getTime() - b.date.getTime());
  return allOccurrences;
};

// The rest remains the same
export const getMonthNames = (startDate: Date): string[] => {
  const monthNames = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ];
  return Array.from({ length: 6 }, (_, i) => monthNames[(startDate.getMonth() + i) % 12]);
};

export const getMonthColor = (dateString: string) => {
  const date = new Date(dateString);
  const monthIndex = date.getMonth();
  return monthColors[monthIndex % monthColors.length];
};

const monthColors = [
  'bg-green-400',
  'bg-pink-500',
  'bg-teal-300',
  'bg-blue-200',
  'bg-zinc-300',
  'bg-yellow-400',
];

export const hasMultipleDates = (eventDates: EventDates[]) => {
  if (!eventDates || eventDates.length === 0) return false;
  if (eventDates.length > 1) return true;
  if (eventDates.length === 1 && eventDates[0].frequency?.rrule) return true;
  return false;
};

// Helper to format date in a consistent way
export const formatEventDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};
